import { useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { UserStorageApi, data } from '@portal/pkg-api/shared'
import { UserStorage } from '@portal/pkg-api/gen/navigator'
import { debounce } from 'lodash-es'

const RECENTLY_VIEWED_KEY = 'RECENTLY_VIEWED_ENTITIES'
const prefix = 'shared.recentlyViewed'

export type EntityTypeId = number
export type RecentlyViewedMap = Record<string, RecentlyViewedEntity[]>

export interface RecentlyViewedEntity {
  id: number
  name: string
  entityTypeId: number
}

// Query definitions following the established pattern
export const RecentlyViewedQueries = {
  recentlyViewed: () => ({
    queryKey: [`${prefix}.recentlyViewed`],
    queryFn: async (): Promise<RecentlyViewedMap> => {
      try {
        const userStorage = await data(
          UserStorageApi.getUserStorageByKey(RECENTLY_VIEWED_KEY as any),
        )
        if (userStorage?.value) {
          return typeof userStorage.value === 'string'
            ? JSON.parse(userStorage.value)
            : (userStorage.value as RecentlyViewedMap)
        }
        return {}
      } catch (error) {
        // If not found (404) or any other error, return empty state
        console.warn('Failed to load recently viewed entities:', error)
        return {}
      }
    },
    retry: (failureCount: number, error: any) => {
      // Don't retry if the key doesn't exist (404)
      if (error?.response?.status === 404) {
        return false
      }
      return failureCount < 3
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 30, // 30 minutes
  }),
}

export function useRecentlyViewedEntities() {
  const queryClient = useQueryClient()

  // Query for getting recently viewed entities
  const {
    data: recentlyViewedMap = {},
    isLoading,
    error,
  } = useQuery(RecentlyViewedQueries.recentlyViewed())

  // Debounced save mutation to prevent rapid saves
  const debouncedSaveMutation = useMemo(
    () =>
      debounce(async (map: RecentlyViewedMap) => {
        const userStorageData: UserStorage = {
          key: RECENTLY_VIEWED_KEY as any,
          value: JSON.stringify(map) as any,
        }
        await data(UserStorageApi.updateUserStorage(userStorageData))
      }, 300),
    [],
  )

  // Mutation for saving recently viewed entities
  const { mutate: saveRecentlyViewed, isPending: saveIsPending } = useMutation({
    mutationFn: async (map: RecentlyViewedMap) => {
      await debouncedSaveMutation(map)
    },
    onError: (error) => {
      console.error('Failed to save recently viewed entities:', error)
    },
  })

  // Mutation for clearing recently viewed entities
  const { mutate: clearRecentlyViewed, isPending: clearIsPending } = useMutation({
    mutationFn: async () => {
      await data(UserStorageApi.deleteUserStorage(RECENTLY_VIEWED_KEY as any))
    },
    onSuccess: () => {
      queryClient.setQueryData([`${prefix}.recentlyViewed`], {})
    },
    onError: (error) => {
      console.error('Failed to clear recently viewed entities:', error)
    },
  })

  const setRecentlyViewed = useCallback(
    (entity: RecentlyViewedEntity) => {
      const { entityTypeId, id } = entity
      const key = String(entityTypeId)

      const updatedMap = { ...recentlyViewedMap }
      const list = updatedMap[key] || []
      const updatedList = list.filter((e: RecentlyViewedEntity) => e.id !== id)
      updatedList.unshift(entity)
      const limitedList = updatedList.slice(0, 10)

      updatedMap[key] = limitedList

      // Optimistically update the cache
      queryClient.setQueryData(RecentlyViewedQueries.recentlyViewed().queryKey, updatedMap)

      // Save to server
      saveRecentlyViewed(updatedMap)
    },
    [recentlyViewedMap, queryClient, saveRecentlyViewed],
  )

  const recentlyViewed = useCallback(
    (entityTypeId: EntityTypeId): RecentlyViewedEntity[] => {
      return recentlyViewedMap[String(entityTypeId)] || []
    },
    [recentlyViewedMap],
  )

  const removeEntity = useCallback(
    (entityTypeId: EntityTypeId, entityId: number) => {
      const key = String(entityTypeId)
      const list = recentlyViewedMap[key] || []
      const updatedList = list.filter((e: RecentlyViewedEntity) => e.id !== entityId)

      const updatedMap = {
        ...recentlyViewedMap,
        [key]: updatedList,
      }

      // Optimistically update the cache
      queryClient.setQueryData(RecentlyViewedQueries.recentlyViewed().queryKey, updatedMap)

      // Save to server
      saveRecentlyViewed(updatedMap)
    },
    [recentlyViewedMap, queryClient, saveRecentlyViewed],
  )

  const clearAll = useCallback(() => {
    // Optimistically update the cache
    queryClient.setQueryData([`${prefix}.recentlyViewed`], {})

    // Clear on server
    clearRecentlyViewed()
  }, [queryClient, clearRecentlyViewed])

  return useMemo(
    () => ({
      recentlyViewedMap,
      setRecentlyViewed,
      recentlyViewed,
      removeEntity,
      clearAll,
      isLoading,
      error,
      isSaving: saveIsPending,
      isClearing: clearIsPending,
    }),
    [
      recentlyViewedMap,
      setRecentlyViewed,
      recentlyViewed,
      removeEntity,
      clearAll,
      isLoading,
      error,
      saveIsPending,
      clearIsPending,
    ],
  )
}
